import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../services/api';

export type TrackWearableMatch = {
  wearable_id: string;
  confidence: number;
};

export type TrackConfidenceMap = Record<string, TrackWearableMatch>;

function isAuthHttpError(e: unknown): boolean {
  if (!axios.isAxiosError(e)) return false;
  const status = e.response?.status;
  return status === 401 || status === 403;
}

export function useWearableTrackConfidenceMap(enabled = true, intervalMs = 3000) {
  const [map, setMap] = useState<TrackConfidenceMap>({});
  const [error, setError] = useState<string | null>(null);
  const authBlockedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || authBlockedRef.current) return;
    try {
      const data = await api.get<TrackConfidenceMap>(
        '/api/wearable-tracking/track-confidence-map',
        undefined,
        { silentError: true },
      );
      setMap(data ?? {});
      setError(null);
    } catch (e) {
      if (isAuthHttpError(e)) {
        authBlockedRef.current = true;
        setError('auth_required');
        return;
      }
      setError(e instanceof Error ? e.message : 'track map fetch failed');
    }
  }, [enabled]);

  useEffect(() => {
    authBlockedRef.current = false;
    if (!enabled) return undefined;
    refresh();
    const t = setInterval(refresh, intervalMs);
    return () => clearInterval(t);
  }, [enabled, intervalMs, refresh]);

  return { map, error, refresh };
}
